import React, { useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import useCallbackRef from 'hooks/useCallbackRef';
import { Button } from 'ncoded-component-library';
import PlusIcon from 'icons/Plus.icon';
import AddABrokerModal from '../../components/AddABrokerModal';
import { InvitedBroker } from '../../models/InvitedBroker';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import useSearchParams from 'hooks/useSearchParams';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  HEADING_TRANSLATIONS,
  TABLE_COLUMNS,
} from './components/BrokerListTableCells/data';
import { BODY_CONTENT_GENERATE } from './components/BrokerListTableCells';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import useMatchMedia from 'hooks/useMatchMedia';

import './BrokerList.styles.scss';

type BrokerListProps = {
  className?: string;
};

const BrokerList: React.FC<BrokerListProps> = (props) => {
  const { className } = props;
  const { t } = useTranslation();
  const [addABrokerModal, addABrokerModalRef] = useCallbackRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [invitedBrokers, setInvitedBrokers] = useState<Array<InvitedBroker>>(
    [],
  );
  const { popServerError } = useContext(PopNotificationsContext);
  const [totalPages, setTotalPages] = useState(0);

  const { limit, updateLimit } = useNumberOfResultsPerPage();

  const classes = classNames('broker-list', className);

  const isPhablet = useMatchMedia('isPhablet');

  const searchParams = useSearchParams();

  const renderedContent = useMemo(
    () =>
      TABLE_COLUMNS.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (broker: InvitedBroker) =>
            BODY_CONTENT_GENERATE[col]({
              broker,
            }),
        }),
        {},
      ),
    [],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const {
          data: { items, totalPages },
        } = await api.portfolio.admin.getInvitedBrokers({
          $limit: limit,
          ...searchParams,
        });

        setTotalPages(totalPages);
        setInvitedBrokers(items);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [searchParams, limit, popServerError]);

  return (
    <div className={classes}>
      <div className="broker-list__heading">
        <h1>{t('brokerList')}</h1>
        <NumberOfResultsPerPageFilter limit={limit} updateLimit={updateLimit} />
        <Button icon={PlusIcon} onClick={() => addABrokerModal?.open()}>
          {t(isPhablet ? 'add' : 'addABroker')}
        </Button>
      </div>
      <div>
        <ReorderResizeTable
          columns={TABLE_COLUMNS}
          isDragDisabled
          data={invitedBrokers}
          isDataLoading={isLoading}
          totalPages={totalPages}
          headingTranslations={HEADING_TRANSLATIONS}
          tableContentTransformations={renderedContent}
        />
      </div>
      <AddABrokerModal
        ref={addABrokerModalRef}
        addInvitedBroker={(broker) =>
          setInvitedBrokers((old) => [...old, broker])
        }
      />
    </div>
  );
};

export default BrokerList;
